import React from 'react';
import { motion } from 'framer-motion';

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const rightItemVariants = {
    hidden: { x: 50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen py-20 px-4 transition-all duration-500">
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid lg:grid-cols-2 gap-16 items-center"
        >
          {/* Left Side - Portrait/Avatar */}
          <motion.div variants={itemVariants} className="relative">
            <div className="relative">
              {/* Main avatar container */}
              <div className="relative w-80 h-80 mx-auto lg:mx-0">
                {/* Background gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-cyan-400 rounded-full blur-2xl opacity-30" />
                
                {/* Avatar placeholder - you can replace with actual image */}
                <div className="relative w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center border-4 border-blue-500/30">
                  <div className="text-8xl font-clash font-bold gradient-text">S</div>
                </div>
                
                {/* Floating elements */}
                <motion.div
                  className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r from-cyan-500 to-emerald-400 rounded-full flex items-center justify-center"
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 5, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </motion.div>
                
                <motion.div
                  className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-r from-emerald-400 to-cyan-500 rounded-full flex items-center justify-center"
                  animate={{
                    y: [0, 10, 0],
                    rotate: [0, -5, 0],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5,
                  }}
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                  </svg>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Content */}
          <motion.div variants={rightItemVariants} className="space-y-8">
            {/* Section Title */}
            <div>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-clash font-bold mb-6">
                Who's Behind the <span className="gradient-text">Code</span>
              </h2>
            </div>

            {/* Story Paragraphs */}
            <div className="space-y-6 text-lg text-gray-300 dark:text-gray-300 text-gray-800 font-inter leading-relaxed">
              <p>
                I'm a full-stack developer passionate about turning ideas into engaging digital experiences.
              </p>

              <p>
                I believe great software isn't just functional, it should feel intuitive, inspiring, and built with care. With over five years of hands-on experience in React, Next.js, Node.js, and modern cloud technologies, I help businesses design and develop products that truly connect with their users.
              </p>

              <p>
                Whether it's building a dynamic web app, creating beautiful Android and iOS apps, optimizing performance, or crafting a seamless user experience, I bring a mix of creativity, precision, and reliability to every project.
              </p>

              <p>
                When I'm not coding, I love exploring new design patterns, contributing to open-source, and sharing what I've learned with other developers.
              </p>
            </div>

            {/* Tech Stack */}
            <div className="pt-6">
              <h3 className="text-2xl font-clash font-semibold text-white dark:text-white text-black mb-4">
                Favorite Stack
              </h3>
              <div className="flex flex-wrap gap-3">
                {['React', 'React Native', 'Flutter', 'Kotlin', 'Next.js', 'TypeScript', 'Node.js', 'PostgreSQL', 'AWS', 'Docker', 'Framer Motion', 'Prisma ORM', 'Tailwind CSS', 'Vercel', 'Motoko', 'ICP'].map((tech, index) => (
                  <span
                    key={tech}
                    className="px-4 py-2 bg-gradient-to-r from-blue-600/20 to-cyan-400/20 text-blue-300 dark:text-blue-300 text-blue-800 rounded-full border border-blue-500/30 dark:border-blue-500/30 border-blue-400/50 hover:border-blue-400/50 dark:hover:border-blue-400/50 transition-colors"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* Signature */}
            <div className="pt-8">
              <div className="relative">
                <div className="text-3xl font-clash font-light text-gray-400 dark:text-gray-400 text-gray-600">
                  — Shubham
                </div>
                <div className="absolute -bottom-2 left-0 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-400 w-full" />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
